import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("de-DE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDateRange = (start, end) => {
  if (!start && !end) return "—";
  if (start && !end) return formatDate(start);
  if (!start && end) return formatDate(end);
  return `${formatDate(start)} – ${formatDate(end)}`;
};

export const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0,
  );

export const getPaymentMethodLabel = (method) => {
  if (method === "sepa") return "SEPA-Lastschrift";
  if (method === "bank_transfer") return "Überweisung";
  return "Nicht erforderlich";
};

export const toLabel = (value) => {
  if (!value) return "Veranstaltung";
  const n = String(value).toUpperCase();
  if (n === "TESTING") return "Prüfung";
  if (n === "SEMINAR") return "Seminar";
  return value;
};

export const toDisplayName = (member) => {
  const full = `${member?.firstName || ""} ${member?.lastName || ""}`.trim();
  return full || member?.memberNumber || "Mitglied";
};

export const getInitials = (member) => {
  const first = (member?.firstName || "").charAt(0).toUpperCase();
  const last = (member?.lastName || "").charAt(0).toUpperCase();
  return first + last || "M";
};

export const getEventTypeColor = (type) => {
  if (!type) return "info";
  const t = String(type).toUpperCase();
  if (t === "TESTING") return "primary";
  if (t === "SEMINAR") return "secondary";
  return "info";
};

export const isTestingType = (type) => String(type || "").toUpperCase() === "TESTING";

export const formatAddressLine = (event = {}) => {
  const street = String(event.street || "").trim();
  const zip = String(event.zip || "").trim();
  const city = String(event.city || "").trim();
  const country = String(event.country || "").trim();
  const zipCity = [zip, city].filter(Boolean).join(" ");
  return [street, zipCity, country].filter(Boolean).join(", ");
};

export const toMultilineText = (value) =>
  String(value || "")
    .replace(/\r\n/g, "\n")
    .trim();

export const toDescriptionPreview = (value, maxLength = 220) => {
  const text = toMultilineText(value).replace(/\n+/g, " ");
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}…`;
};

export const getEventSelectableMembers = (event = {}) => {
  const preferred = Array.isArray(event.participantEligibleMembers)
    ? event.participantEligibleMembers
    : [];
  if (preferred.length > 0) return preferred;
  if (event.allowSelfRegistration === false) {
    return Array.isArray(event.contractMembersOnAttendeeList)
      ? event.contractMembersOnAttendeeList
      : [];
  }
  return Array.isArray(event.contractMembers) ? event.contractMembers : [];
};

export const roundCurrency = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

export const getEventPricingOptions = (event = {}) =>
  (Array.isArray(event?.pricingOptions) ? event.pricingOptions : [])
    .map((option) => ({
      optionId: String(option?.optionId || "").trim(),
      label: String(option?.label || "").trim() || "Teilnahmegebühr",
      amount: roundCurrency(option?.amount),
    }))
    .filter((option) => option.optionId);

export const getDefaultPricingOptionId = (event = {}) =>
  getEventPricingOptions(event)[0]?.optionId || "";

export const toUniqueMemberIdsPreserveOrder = (ids = []) =>
  Array.from(
    new Set((Array.isArray(ids) ? ids : []).map((id) => String(id)).filter(Boolean)),
  );

export const toSortedUniqueMemberIds = (ids = []) =>
  [...toUniqueMemberIdsPreserveOrder(ids)].sort();

export const toValidTimestampOrInfinity = (value) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

export const calculateStandardEventPricingPreview = ({
  event,
  selectedMemberIds = [],
  selectedPricingOptionIdByMember = {},
}) => {
  const pricingOptions = getEventPricingOptions(event);
  const optionAmountById = new Map(
    pricingOptions.map((option) => [option.optionId, roundCurrency(option.amount)]),
  );
  const fallbackFeeAmount = roundCurrency(event?.feeAmount);
  const defaultPricingOptionId = getDefaultPricingOptionId(event);

  const siblingEnabled =
    Boolean(event?.siblingDiscount?.enabled) && selectedMemberIds.length > 1;
  const siblingType =
    String(event?.siblingDiscount?.type || "").trim().toLowerCase() === "fixed"
      ? "fixed"
      : "percentage";
  const siblingValue = Math.max(0, Number(event?.siblingDiscount?.value) || 0);
  const siblingPercentage = Math.min(100, siblingValue);

  const amountByMember = {};
  const optionIdByMember = {};
  let totalAmount = 0;
  const existingParticipationByMemberId = new Map(
    (Array.isArray(event?.myParticipations) ? event.myParticipations : [])
      .map((entry) => [String(entry?.memberId || ""), entry])
      .filter(([memberId]) => Boolean(memberId)),
  );
  const orderedMembers = selectedMemberIds
    .map((memberId, index) => {
      const existingParticipation = existingParticipationByMemberId.get(String(memberId)) || null;
      return {
        memberId: String(memberId),
        index,
        createdAt: existingParticipation?.createdAt,
      };
    })
    .sort((left, right) => {
      const leftTimestamp = toValidTimestampOrInfinity(left.createdAt);
      const rightTimestamp = toValidTimestampOrInfinity(right.createdAt);
      const leftHasCreatedAt = Number.isFinite(leftTimestamp);
      const rightHasCreatedAt = Number.isFinite(rightTimestamp);

      if (leftHasCreatedAt && rightHasCreatedAt) {
        if (leftTimestamp !== rightTimestamp) return leftTimestamp - rightTimestamp;
      } else if (leftHasCreatedAt) {
        return -1;
      } else if (rightHasCreatedAt) {
        return 1;
      }

      if (left.index !== right.index) return left.index - right.index;
      return String(left.memberId).localeCompare(String(right.memberId));
    });

  orderedMembers.forEach((entry, index) => {
    const memberId = entry.memberId;
    let selectedPricingOptionId = String(
      selectedPricingOptionIdByMember?.[memberId] || "",
    ).trim();

    if (selectedPricingOptionId && !optionAmountById.has(selectedPricingOptionId)) {
      selectedPricingOptionId = "";
    }
    if (!selectedPricingOptionId && defaultPricingOptionId) {
      selectedPricingOptionId = defaultPricingOptionId;
    }

    const baseAmount =
      selectedPricingOptionId && optionAmountById.has(selectedPricingOptionId)
        ? optionAmountById.get(selectedPricingOptionId)
        : fallbackFeeAmount;

    let paidAmount = baseAmount;
    if (siblingEnabled && index > 0) {
      if (siblingType === "fixed") {
        paidAmount = Math.max(0, baseAmount - siblingValue);
      } else {
        paidAmount = Math.max(0, baseAmount * (1 - siblingPercentage / 100));
      }
    }

    const normalizedPaidAmount = roundCurrency(paidAmount);
    amountByMember[memberId] = normalizedPaidAmount;
    optionIdByMember[memberId] = selectedPricingOptionId || undefined;
    totalAmount = roundCurrency(totalAmount + normalizedPaidAmount);
  });

  return {
    amountByMember,
    optionIdByMember,
    totalAmount,
  };
};

export const getInitialStateByEvent = (events = []) =>
  (events || []).reduce((acc, event) => {
    const selectedMemberIds = (event.myParticipations || []).map((e) => String(e.memberId));
    const photoOptOutByMember = {};
    const selectedPricingOptionIdByMember = {};
    const defaultPricingOptionId = getDefaultPricingOptionId(event);

    getEventSelectableMembers(event).forEach((m) => {
      photoOptOutByMember[String(m._id)] = false;
      if (defaultPricingOptionId) {
        selectedPricingOptionIdByMember[String(m._id)] = defaultPricingOptionId;
      }
    });
    (event.myParticipations || []).forEach((e) => {
      const memberId = String(e.memberId);
      photoOptOutByMember[memberId] = Boolean(e.photoOptOut);
      if (e.selectedPricingOptionId) {
        selectedPricingOptionIdByMember[memberId] = String(e.selectedPricingOptionId);
      } else if (defaultPricingOptionId) {
        selectedPricingOptionIdByMember[memberId] = defaultPricingOptionId;
      }
    });
    acc[String(event._id)] = {
      selectedMemberIds,
      photoOptOutByMember,
      selectedPricingOptionIdByMember,
    };
    return acc;
  }, {});

// ─────────────────────────────────────────────────────────────────────────────
// RSVP Status Config
// ─────────────────────────────────────────────────────────────────────────────

export const RSVP_CONFIG = {
  zugesagt: {
    label: "Zugesagt",
    color: "success",
    icon: "mdi:check-circle",
    bgColor: "success.main",
  },
  abgesagt: {
    label: "Abgesagt",
    color: "error",
    icon: "mdi:close-circle",
    bgColor: "error.main",
  },
  ausstehend: {
    label: "Ausstehend",
    color: "default",
    icon: "mdi:clock-outline",
    bgColor: "text.disabled",
  },
};

export const BOOKING_STATUS_MAP = {
  submitted:  { label: "Angemeldet – Bestätigung ausstehend", color: "warning", icon: "mdi:clock-outline" },
  confirmed:  { label: "Anmeldung bestätigt",                 color: "success", icon: "mdi:check-circle" },
  waitlist:   { label: "Warteliste",                          color: "info",    icon: "mdi:account-clock" },
  cancelled:  { label: "Storniert",                           color: "error",   icon: "mdi:close-circle" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

export const EventMetaRow = ({ icon, children, highlight = false }) => (
  <Stack direction="row" spacing={0.75} alignItems="flex-start">
    <Icon
      icon={icon}
      width={15}
      color={highlight ? "#ed6c02" : "#94a3b8"}
      style={{ marginTop: 2, flexShrink: 0 }}
    />
    <Typography
      variant="caption"
      color={highlight ? "warning.main" : "text.secondary"}
      fontWeight={highlight ? 600 : 500}
      sx={{ lineHeight: 1.45 }}
    >
      {children}
    </Typography>
  </Stack>
);

export const DetailSection = ({ icon, title, children }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
      <Icon icon={icon} width={16} color="#64748b" />
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

/**
 * Initialen-Avatar für ein Mitglied
 */
export const MemberAvatar = ({ member, size = 36 }) => {
  const initials = getInitials(member);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "primary.contrastText",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: 0.5,
      }}
    >
      {initials}
    </Box>
  );
};

/**
 * RSVP-Karte für ein einzelnes Mitglied bei Prüfungsereignissen.
 * Zeigt Name, Status und ZUSAGEN/ABSAGEN-Buttons.
 */
export const RsvpMemberCard = ({ member, eventId, isSaving, onRsvp, onPhotoOptOutChange }) => {
  const rsvpStatus = member?.rsvpStatus || "ausstehend";
  const cfg = RSVP_CONFIG[rsvpStatus] || RSVP_CONFIG.ausstehend;
  const memberId = String(member._id);
  const isSavingZusagen = isSaving === "zusagen";
  const isSavingAbsagen = isSaving === "absagen";
  const anyLoading = isSavingZusagen || isSavingAbsagen;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: rsvpStatus === "zugesagt"
          ? "success.light"
          : rsvpStatus === "abgesagt"
          ? "error.light"
          : "divider",
        bgcolor: rsvpStatus === "zugesagt"
          ? "success.lighter" // may not exist in all themes, fallback below
          : rsvpStatus === "abgesagt"
          ? "error.lighter"
          : "background.paper",
        transition: "border-color 0.2s, background-color 0.2s",
      }}
    >
      <Stack spacing={1}>
        {/* Mitglied-Zeile */}
        <Stack direction="row" spacing={1.25} alignItems="center">
          <MemberAvatar member={member} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {toDisplayName(member)}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
              <Icon icon={cfg.icon} width={14} color={`var(--mui-palette-${cfg.color}-main, #888)`} />
              <Typography
                variant="caption"
                color={rsvpStatus === "ausstehend" ? "text.secondary" : `${cfg.color}.main`}
                fontWeight={500}
              >
                {cfg.label}
              </Typography>
            </Stack>
          </Box>

          {/* Aktions-Buttons */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip
              title="Teilnahme bestätigen – ich werde an der Prüfung teilnehmen"
              arrow
              placement="top"
            >
              <span>
                <Button
                  size="small"
                  variant={rsvpStatus === "zugesagt" ? "contained" : "outlined"}
                  color="success"
                  disabled={anyLoading || rsvpStatus === "zugesagt"}
                  onClick={() => onRsvp(memberId, "zusagen")}
                  startIcon={
                    isSavingZusagen ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Icon icon="mdi:check" width={16} />
                    )
                  }
                  sx={{ minWidth: 0, px: 1.5, fontWeight: 600 }}
                >
                  Zusagen
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title="Teilnahme absagen – ich kann leider nicht teilnehmen"
              arrow
              placement="top"
            >
              <span>
                <Button
                  size="small"
                  variant={rsvpStatus === "abgesagt" ? "contained" : "outlined"}
                  color="error"
                  disabled={anyLoading || rsvpStatus === "abgesagt"}
                  onClick={() => onRsvp(memberId, "absagen")}
                  startIcon={
                    isSavingAbsagen ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Icon icon="mdi:close" width={16} />
                    )
                  }
                  sx={{ minWidth: 0, px: 1.5, fontWeight: 600 }}
                >
                  Absagen
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Foto-Einwilligung – nur nach Zusage */}
        {rsvpStatus === "zugesagt" && (
          <FormControlLabel
            sx={{ m: 0, ml: 0.5 }}
            control={
              <Checkbox
                size="small"
                checked={!Boolean(member.photoOptOut)}
                onChange={(e) => onPhotoOptOutChange(memberId, !e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Bildverwendung für Werbung / Social Media erlaubt
              </Typography>
            }
          />
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Prüfungs-Info-Banner, der erklärt, was eine Aufstellung bedeutet.
 */
export const TestingInfoBanner = ({ membersOnList }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.75,
      borderRadius: 2,
      borderColor: "primary.light",
      bgcolor: "primary.lighter",
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(25, 118, 210, 0.12)"
          : "rgba(25, 118, 210, 0.06)",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ pt: 0.25, flexShrink: 0 }}>
        <Icon icon="mdi:medal" width={28} color="#1976d2" />
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 0.4 }}>
          Prüfungsaufstellung
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {membersOnList > 0
            ? `${membersOnList === 1 ? "Ein Mitglied aus deinem Vertrag steht" : `${membersOnList} Mitglieder aus deinem Vertrag stehen`} auf der Prüfungsliste. Bitte gib dem Trainer eine Rückmeldung, ob die Teilnahme möglich ist.`
            : "Kein Mitglied aus deinem Vertrag wurde bisher zur Prüfung aufgestellt. Sobald dein Trainer jemanden aufstellt, erscheint hier die Benachrichtigung."}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);
